import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiFetch } from './client'
import type { ApiError } from './types'

export interface UserPreferences {
  theme_id: string
  /** IANA name, browser-supplied. 'UTC' until the browser syncs it. */
  timezone: string
  notifications_enabled: boolean
  /** Missing key means enabled. Only 'reminders' is surfaced in the UI. */
  notification_categories: Record<string, boolean>
  updated_at: string
}

export type UserPreferencesPatch = Partial<Omit<UserPreferences, 'updated_at'>>

export function fetchPreferences(): Promise<UserPreferences> {
  return apiFetch<UserPreferences>('/preferences')
}

/** PUT is a patch: omitted fields keep their stored value. */
export function updatePreferences(patch: UserPreferencesPatch): Promise<UserPreferences> {
  return apiFetch<UserPreferences>('/preferences', {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

/** The shared ['preferences'] query. Anything that needs the stored
 * preferences reads this cache rather than fetching its own copy, so the
 * notification toggle and the theme sync can never disagree. */
export function usePreferences(): UseQueryResult<UserPreferences, ApiError> {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    // Updates flow through mutations that invalidate this key, not through
    // background refetches.
    staleTime: Infinity,
  })
}
